import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { MutationHookOptions } from '@apollo/client/react/types/types';

import {
  cobaltIntelligenceApi,
  PullSecretaryOfStateSearchReportResponse,
  PullSecretaryOfStateSearchReportVariables,
} from '../api';

export function useCobaltIntelligence() {
  const { showNotification } = useNotifications();

  const [pullReport, { loading: isPulling }] = useMutation<
    PullSecretaryOfStateSearchReportResponse,
    PullSecretaryOfStateSearchReportVariables
  >(cobaltIntelligenceApi.pullSecretaryOfStateSearchReport);

  const handlePullReport = useCallback(
    async (
      dto: { voId: string; isMock?: boolean },
      fetchOptions?: (api: typeof cobaltIntelligenceApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await pullReport({
          variables: { input: { ...dto, isMock: !!dto?.isMock } },
          ...(typeof fetchOptions == 'function' ? fetchOptions(cobaltIntelligenceApi) : {}),
        });

        const result = data?.cobaltIntelligence?.result;

        if (!result?.success || result.error) {
          showNotification(result?.error ?? 'Failed to generate report', { type: 'error' });
          return null;
        }

        showNotification('Report generated successfully');
        return result.entity;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong while generating the report', { type: 'error' });
        return null;
      }
    },
    [pullReport, showNotification]
  );

  return { isPulling, handlePullReport };
}
